import {
  LearnMoreLink,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Grid,
  InputField,
  DropdownSelect,
  FormControlLabel,
  DialogTitle,
  Typography,
  ActiveSwitch
} from '@paintscout/ui'
import { getFeatures, getObjectLabels, updateProductOptions, type ProductOption } from '@paintscout/util/builder'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses, DialogProps, DropdownSelectOption } from '@paintscout/ui'
import type { OptionsDocument } from 'paintscout'
import { makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import get from 'lodash/get'
import find from 'lodash/find'
import { useFlags } from 'shared/components/common/FlagsProvider'
import classNames from 'classnames'
import QuoteTypeSelect from '../../QuoteTypeSelect'
import { CloseButton } from '../../common'

const useStyles = makeStyles<Theme, EditProductOptionDialogProps>(
  (theme) => ({
    root: {
      overflow: 'visible',
      position: 'relative'
    },
    dialogTitle: {},
    dialogPaper: {
      overflow: 'visible',
      position: 'relative'
    },
    dialogContent: {
      maxHeight: '70vh',
      overflow: 'visible',
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '75vh'
      }
    },
    switchLabel: {
      marginLeft: '0 !important',
      marginRight: 0
    },
    cancel: {},
    confirm: {},
    overflowVisible: {
      overflow: 'visible'
    },
    customUnit: {
      padding: theme.spacing(2)
    },
    featureProductCheckDiv: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      minHeight: '90px',
      [theme.breakpoints.down('sm')]: {
        minHeight: '50px'
      }
    },
    avatarDiv: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start'
      }
    }
  }),
  { name: 'EditProductOptionDialog' }
)

export interface EditProductOptionDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  item: ProductOption
  options: OptionsDocument
  isNew?: boolean
  onConfirm: (options: OptionsDocument, isDirty?: boolean) => void
  onCancel: () => void
}

function EditProductOptionDialog(props: EditProductOptionDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, options, item, isNew, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const [isDirty, setisDirty] = useState(false)
  const [productOption, setProductOption] = useState<ProductOption>(item)
  const objectLabels = getObjectLabels({ options })
  const features = getFeatures({ options })
  const materialCostFeature = get(features, 'quotes.materialCost.enabled', false)
  const { sherwinWilliams, canDeactivateSettingsItems } = useFlags()
  const isActive = productOption.active ?? true
  const isFeatureProduct = productOption.featureProduct

  const productUnitOptions = [
    { label: 'gal', value: 'gal' },
    { label: 'item', value: 'item' },
    { label: 'Custom...', value: '__custom__' }
  ]
  let selectedProductUnitOption = null

  selectedProductUnitOption = find(productUnitOptions, { value: productOption.unitLabel })
  if (!selectedProductUnitOption && typeof productOption.unitLabel === 'undefined') {
    selectedProductUnitOption = productUnitOptions[0]
  }

  const unitLabel = selectedProductUnitOption?.label ?? productOption.unitLabel

  const handleToggle = () => {
    setProductOption({ ...productOption, active: !isActive })
    setisDirty(true)
  }

  const leftButton = canDeactivateSettingsItems ? (
    <FormControlLabel
      className={classes.switchLabel}
      control={<ActiveSwitch checked={isActive} onChange={handleToggle} />}
      label={'Active'}
      labelPlacement={'end'}
    />
  ) : undefined

  const handleQuoteTypeChange = (values: string[]) => {
    setProductOption({ ...productOption, quoteTypes: values })
    setisDirty(true)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setProductOption({ ...productOption, [name]: value })
    setisDirty(true)
  }

  const handleUnitLabelChange = (option: DropdownSelectOption) => {
    setProductOption({ ...productOption, unitLabel: option.value === '__custom__' ? '' : option.value })
    setisDirty(true)
  }

  const handleUnitLabelInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target
    setProductOption({ ...productOption, unitLabel: value })
    setisDirty(true)
  }

  const handleConfirm = () => {
    setIsLoading(true)
    try {
      const updatedOptions = updateProductOptions({ options, productOptions: [productOption] })
      onConfirm(updatedOptions, isDirty)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }

  // const handleAvatarUpload = ({
  //   src: newSrc,
  //   format,
  //   cloudinaryPublicId,
  //   s3PublicKey,
  //   width,
  //   height
  // }: {
  //   src: string
  //   format: string
  //   cloudinaryPublicId: string
  //   s3PublicKey: string
  //   width: number
  //   height: number
  // }) => {
  //   const customAvatarValue = {
  //     value: newSrc,
  //     cdnValue: newSrc,
  //     cloudinaryId: cloudinaryPublicId,
  //     s3PublicKey,
  //     width,
  //     height,
  //     fileType: format
  //   }
  //   setProductOption({ ...productOption, featureImage: customAvatarValue })
  //   setisDirty(true)
  // }

  // const handleAvatarClear = () => {
  //   const customAvatarValue = {
  //     value: '',
  //     cdnValue: null,
  //     cloudinaryId: null,
  //     s3PublicKey: null,
  //     fileType: null
  //   }
  //   setProductOption({ ...productOption, featureImage: customAvatarValue })
  //   setisDirty(true)
  // }

  return (
    <Dialog
      classes={{ root: classes.root, paper: classes.dialogPaper }}
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      {...baseDialogProps}
    >
      <DialogTitle
        rightContent={<CloseButton onCancel={onCancel} />}
        loading={isLoading}
        subtitle={productOption.manufacturer || null}
      >{`${isNew ? 'Add' : 'Edit'} Product`}</DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form
          id="edit-product-option-dialog-form"
          onSubmit={(e) => {
            e.preventDefault()
            handleConfirm()
          }}
        >
          <Grid container={true} spacing={3} justifyContent="flex-start" alignItems="flex-start">
            <Grid item xs={12} sm={12} md={6}>
              <InputField
                classes={{ root: classes.input }}
                label={'Label or Name'}
                name={'label'}
                disabled={isFeatureProduct}
                value={productOption.label}
                fullWidth
                onChange={handleChange}
                required
                error={!productOption.label}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <QuoteTypeSelect
                label={`${objectLabels.quote.value} Types`}
                variant="multi"
                onChange={handleQuoteTypeChange}
                value={productOption.quoteTypes}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <InputField
                classes={{ root: classes.input }}
                label={`Coverage (${objectLabels.unit.abbreviation.square}/${unitLabel})`}
                name={'coverage_rate'}
                value={productOption.coverage_rate}
                format={'rate'}
                fullWidth
                onChange={handleChange}
                toolTip={`The number of square ${objectLabels.unit.plural.toLowerCase()} that the product can cover with one coat.`}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <InputField
                classes={{ root: classes.input }}
                label={`Coverage (${objectLabels.unit.abbreviation.linear}/${unitLabel})`}
                name={'lnft_coverage'}
                value={productOption.lnft_coverage}
                format={'rate'}
                fullWidth
                onChange={handleChange}
                toolTip={`The number of linear ${objectLabels.unit.plural.toLowerCase()} that the product can cover with one coat.`}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <InputField
                classes={{ root: classes.input }}
                label={`Coverage (${unitLabel}/item)`}
                name={'quantity_coverage'}
                value={productOption.quantity_coverage}
                format={'rate'}
                fullWidth
                onChange={handleChange}
                toolTip={
                  <Grid container>
                    <Grid item>
                      {`How much of the product is being used to cover one item with one coat. (i.e. you use 0.05 of a gallon to cover one door with one coat).`}
                    </Grid>
                    <Grid item>
                      <LearnMoreLink link="http://help.paintscout.com/en/articles/6063072-coverage-rates#h_87aa5a7f20" />
                    </Grid>
                  </Grid>
                }
              />
            </Grid>
            {materialCostFeature && (
              <>
                <Grid item xs={12} sm={12} md={6}>
                  <InputField
                    classes={{ root: classes.input }}
                    label={`Price (${objectLabels.currency.symbol}/${unitLabel})`}
                    name={'price'}
                    format={'rate'}
                    value={productOption.price}
                    fullWidth
                    onChange={handleChange}
                    toolTip={`The price you want to charge your customer for the product.`}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <DropdownSelect
                    variant="single"
                    label={'Unit'}
                    options={productUnitOptions}
                    value={selectedProductUnitOption || { label: unitLabel, value: '__custom__' }}
                    onChange={(selectedOption) => handleUnitLabelChange(selectedOption ?? productUnitOptions[0])}
                    fullWidth
                    toolTip={'Specify how the product is measured. Select custom to add a different type of unit.'}
                    checkInvalid={() => (unitLabel.length > 0 ? '' : 'Unit label is required')}
                    pagination={{
                      currentPage: !selectedProductUnitOption ? 1 : 0,
                      pages: [
                        <Grid container className={classes.customUnit} key="custom-unit">
                          <InputField
                            autoFocus
                            classes={{ root: classes.input }}
                            label={'Custom Unit'}
                            fullWidth
                            value={unitLabel}
                            onChange={handleUnitLabelInputChange}
                            required
                          />
                        </Grid>
                      ],
                      onPageChange: (_) => {
                        // selectedProductUnitOption resets when we change pages, so we don't need to do anything here
                        // if we don't pass a function the dropdown will just close
                        return
                      }
                    }}
                  />
                </Grid>
              </>
            )}
            {sherwinWilliams && isFeatureProduct && (
              <>
                {/* <Grid className={classes.featureProductCheckDiv} item xs={12} sm={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isFeatureProduct}
                        onChange={() => {
                          setProductOption((currentOption) => {
                            return { ...productOption, featureProduct: !currentOption.featureProduct }
                          })
                          setisDirty(true)
                        }}
                        name={'featureProduct'}
                      />
                    }
                    label={`Feature Product`}
                  />
                </Grid> */}
                {/* {isFeatureProduct && (
                  <>
                    <Grid className={classes.avatarDiv} item xs={12} sm={12} md={4}>
                      <UploadImage
                        productImage
                        cloudinaryPublicId={productOption?.featureImage?.cloudinaryId}
                        s3PublicKey={productOption?.featureImage?.s3PublicKey}
                        src={
                          productOption?.featureImage?.value ??
                          productOption?.featureImage?.s3PublicKey ??
                          productOption?.featureImage?.cloudinaryId
                        }
                        onUpload={handleAvatarUpload}
                        onClear={() => handleAvatarClear()}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                      <InputField
                        multiline
                        classes={{ root: classes.input }}
                        label={`Description`}
                        name={'description'}
                        value={productOption.description}
                        fullWidth
                        onChange={handleChange}
                        toolTip={`The description shown alongside the product in the product table.`}
                      />
                    </Grid>
                  </>
                )} */}
                <Grid item xs={12} sm={'auto'}>
                  <img src={productOption.featureImage.value} alt={productOption.label} style={{ height: 120 }} />
                </Grid>
                <Grid item xs={12} sm>
                  <Typography variant={'h3'} style={{ marginTop: 2, marginBottom: 8 }}>
                    {productOption.label}
                  </Typography>
                  <Typography variant={'body1'} style={{ marginBottom: 8 }}>
                    {productOption.description}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions leftButton={leftButton} alignLeftButtonWithTitle={true}>
        <Button
          className={classNames(classes.confirm, 'tour-edit-product-option-dialog-confirm-button')}
          type="submit"
          form="edit-product-option-dialog-form"
          variant={'contained'}
          disabled={isLoading}
          data-testid="edit-product-option-dialog-confirm-button"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditProductOptionDialog
